import { GET, getAPIRequest, PUT } from 'infrastructure/api'
import { IQueryInfo } from 'infrastructure/api'
import { doIngestionQueries } from 'shared/api/ingestion'
import {
  IAttachmentsListNode,
  ICourseNode,
  IQuestionNode,
  ISimplifiedNode,
} from './IngestedTree'
import {
  AwsContentItem,
  AwsListResult,
  LeafBodyValues,
  NodeType,
  QuestionType,
} from 'shared/types/ingestion'
import { allNodesOfType } from '../../ingestion/utility'
import { IQuestionDrawingResponse } from './IngestionQuestions'
import {
  partnerKeyForTree,
  subjectForTree,
} from '../image_verification/utility'
import { getGradeId } from 'shared/utils/tree'
import { INode } from 'shared/types/tree'
import mime from 'mime'
import { gzipSync, strToU8 } from 'fflate'
import { contentServicePost } from 'infrastructure/contentService'

const IngestionBucket = 'kiddom-ingestion'

export const S3Url = `https://${IngestionBucket}.s3.amazonaws.com`

type PresignRequestItem = {
  bucket_name: string
  object_name: string
  headers?: Record<string, string>
}

type PresignResponseItem = {
  presigned_url: string
}

async function getPresignedUploadUrls(
  items: PresignRequestItem[],
): Promise<string[]> {
  const { result } = await contentServicePost<{
    data: PresignResponseItem[]
  }>({
    path: '/content/v1/s3/upload-url',
    body: items,
  })

  if (!result?.data?.length || result.data.length !== items.length) {
    return []
  }

  return result.data.map((item) => item.presigned_url)
}

function partnerPrefix(partner: string) {
  return `partners/${partner}`
}

async function makeS3Request(fullUrl: string) {
  return new Promise<Document | null>((resolve) => {
    const x = new XMLHttpRequest()
    x.open(GET, fullUrl, true)
    x.onreadystatechange = function () {
      if (x.readyState === 4 && x.status === 200) {
        const doc = x.responseXML
        resolve(doc)
      }
    }
    x.send(null)
  })
}

async function makeAllTheRequests(
  fullUrl: string,
  searchParams: URLSearchParams,
) {
  return new Promise<Document[]>((resolve) => {
    const allResults: Document[] = []
    const doNextRequest = async (continueToken?: string) => {
      if (continueToken) {
        searchParams.set('continuation-token', continueToken)
      }
      const url = `${fullUrl}?${searchParams.toString()}`
      const results = await makeS3Request(url)
      const ctElement = results
        ?.getElementsByTagName('NextContinuationToken')
        .item(0)
      const ct = ctElement?.textContent
      results && allResults.push(results)
      if (ct) {
        doNextRequest(ct)
      } else {
        resolve(allResults)
      }
    }
    doNextRequest()
  })
}

async function getS3BucketResponse(prefix: string) {
  const res = await fetch(`${S3Url}/?list-type=2&prefix=${prefix}&delimiter=/`)
  if (!res.ok) throw new Error(`HTTP ${res.status} - ${res.statusText}`)
  const text = await res.text()
  const parser = new DOMParser()
  return parser.parseFromString(text, 'application/xml')
}

export async function getPartnerFolders(onLoaded: (files: Element[]) => void) {
  const doc = await getS3BucketResponse('partners/')
  const keys = doc?.getElementsByTagName('CommonPrefixes')
  if (!keys) return
  const asElements: Element[] = []
  for (let i = 0; i < keys.length; i++) {
    asElements.push(keys[i])
  }
  onLoaded(asElements)
}

export async function getCurriculaContentFolderForTree(
  tree: INode,
  extraBit?: string,
) {
  const subject = await subjectForTree(tree)
  if (!subject) return
  const subjectName = subject.subject_name
  const partnerId = partnerKeyForTree(tree)
  const gradeCode = String(getGradeId(tree))
  return getCurriculaContentFolder({
    partnerId,
    currId: subjectName,
    gradeCode,
    envOverride: 'production',
    extraBit,
  })
}

function convertFolderResponse(s3Url: string, response: Document[]) {
  return response.reduce((acc: Record<string, string>, result) => {
    const keys = result?.getElementsByTagName('Key')
    if (!keys) return acc
    Array.from(keys).forEach((element) => {
      const fileKey = element.textContent
      const fileName = fileKey!.split('/').pop()!
      acc[fileName] = `${s3Url}${fileKey}`
    })
    return acc
  }, {})
}

export async function getCurriculaContentFolder({
  partnerId,
  currId,
  gradeCode,
  envOverride = 'production',
  extraBit,
}: {
  partnerId: string
  currId: string
  gradeCode: string
  envOverride?: string
  extraBit?: string
}): Promise<{ [name: string]: string }> {
  const s3Url = `https://kiddom-media-${envOverride}.s3.amazonaws.com/`
  const searchParams = new URLSearchParams()
  searchParams.append('list-type', '2')
  const prefixPath = `${partnerId}/${currId}/${gradeCode}/${
    extraBit ? `${extraBit}/` : ''
  }`
  searchParams.append('prefix', prefixPath)
  searchParams.append('delimiter', '/')
  const results = await makeAllTheRequests(s3Url, searchParams)
  return convertFolderResponse(s3Url, results)
}

export enum UrlKey {
  ingestionUpload = 'ingestionUploadURL',
  exportUpload = 'exportUploadURL',
}

export function uploadPartnerFile(
  partnerKey: string,
  file: File,
  onLoaded?: () => void,
) {
  return uploadPartnerFileForKey({
    partnerKey,
    blob: file,
    onLoaded,
    urlKey: UrlKey.exportUpload,
    fileName: file.name,
  })
}

function verifyFile(file: File) {
  return new Promise<boolean>((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsText(file)
    reader.addEventListener('load', (event) => {
      const asJson = JSON.parse(event.target?.result!.toString()!)
      if (asJson.type === 'course') {
        return resolve(true)
      }
      reject(new Error('not valid course file'))
    })
  })
}

export function srcsFromString(stringWithUrls: string): string[] {
  const matches = stringWithUrls.matchAll(
    /<(?:img|audio|video) [^>]*src=\\"([^"]*)\\"[^>]*>/gm,
  )
  const urls = new Set()
  Array.from(matches).forEach((match) => urls.add(match[1]))
  return Array.from(urls) as string[]
}

export function svgUseFromString(stringWithUrls: string): string[] {
  const matches = stringWithUrls.matchAll(
    /<(?:use) [^>]*href=\\"([^"]*)\\"[^>]*>/gm,
  )
  const urls = new Set()
  Array.from(matches).forEach((match) => urls.add(match[1].split('#')[0]))
  return Array.from(urls) as string[]
}

export function pdfsFromString(stringWithUrls: string): string[] {
  const matches = JSON.stringify(stringWithUrls).matchAll(
    /<a\s+(?:[^>]*?\s+)?href=(?:[^"]*)"([^\\"]*)/gm,
  )

  const urls = new Set()
  Array.from(matches).forEach((match) => {
    return urls.add(match[1])
  })
  return Array.from(urls) as string[]
}

export function allUrlsInTree(tree: ISimplifiedNode | ICourseNode) {
  const str = JSON.stringify(tree)
  const urls = new Set([
    ...srcsFromString(str),
    ...svgUseFromString(str),
    ...pdfsFromString(str),
  ])

  allNodesOfType<IAttachmentsListNode>(NodeType.AttachmentsList, tree).forEach(
    (list) => {
      list.data.attachments_list.forEach((attachment) => {
        urls.add(attachment.url)
      })
    },
  )
  allNodesOfType<IQuestionNode>(NodeType.Question, tree)
    .filter((n) => n.data.type === QuestionType.Drawing)
    .map((n) => n as IQuestionDrawingResponse)
    .forEach((n) => {
      if (n.data.attachment) {
        if (!n.data.attachment.url) {
          throw new Error('missing attachment url')
        }
        urls.add(n.data.attachment.url)
      }
    })
  return Array.from(urls)
}

export function isInternalUrl(url: string) {
  return (
    url.includes('kiddom-media') ||
    url.includes('api-assignment-assets') ||
    url.includes('kiddom-static')
  )
}

export function isExternalUrl(url: string) {
  return !url.startsWith('data:') && !isInternalUrl(url)
}

/**
 * @deprecated
 */
export async function subjectForCode(subjectId?: number) {
  if (!subjectId) return
  const subjects = await getSubjects()
  return Object.values(subjects).find(
    (subject) => subject.subject_id === subjectId,
  )
}

/**
 * @deprecated
 */
export function subjectForCourse(course: ICourseNode) {
  const subId = course.data[LeafBodyValues.SubjectID]
  return subjectForCode(subId)
}

export async function uploadCourse(
  partnerKey: string,
  course: ICourseNode,
  optionalName?: string,
) {
  return new Promise((resolve) => {
    const asString = JSON.stringify(course)
    const deflated = gzipSync(strToU8(asString))
    const zipBlob = new Blob([deflated as unknown as ArrayBuffer], {
      type: 'application/gzip',
    })
    uploadIngestion(partnerKey, zipBlob, 'json.gz', resolve, optionalName)
  })
}

export function deletePartnerFile(
  partnerKey: string,
  fileName: string,
  key: UrlKey,
  onLoaded: () => void,
) {
  return retrieveIngestionUrlForKey(partnerKey, key).then((uploadUrl) => {
    const x = new XMLHttpRequest()
    const url = `${uploadUrl}/${fileName}`
    x.open('DELETE', url)
    x.onreadystatechange = function () {
      if (x.readyState === 4 && x.status === 204) {
        onLoaded()
      }
    }
    x.send()
  })
}

export function uploadIngestion(
  partnerKey: string,
  blob: Blob,
  fileExt: string,
  onLoaded: (err?: Error | null) => void,
  optionalName?: string,
) {
  const fileName = `${optionalName ?? Math.floor(Date.now() / 1000).toString()}.${fileExt}`
  return uploadPartnerFileForKey({
    partnerKey,
    blob,
    onLoaded,
    urlKey: UrlKey.ingestionUpload,
    fileName,
  })
}

export function createNewPartner(partnerKey: string, onLoaded: () => void) {
  const bucket = new URL(S3Url).host.split('.')[0]
  const objectName = `${partnerPrefix(partnerKey)}/`
  return getPresignedUploadUrls([
    {
      bucket_name: bucket,
      object_name: objectName,
    },
  ]).then(async (urls) => {
    if (!urls[0]) {
      throw new Error(`Failed to get presigned URL for partner: ${partnerKey}`)
    }
    const res = await fetch(urls[0], {
      method: 'PUT',
      body: new Blob([]),
    })
    if (res.ok) {
      onLoaded()
    }
  })
}

export function retrieveIngestedTree(partnerKey: string, treeKey: string) {
  const query: IQueryInfo = {
    resourceData: {
      key: 'ingestedTree',
      fields: ['tree'],
    },
    params: {
      partner_key: partnerKey,
      ingestion_key: treeKey,
    },
  }
  return doIngestionQueries<{ tree: { [key: string]: unknown } }>(
    [query],
    false,
  ).then(({ data, error }) => {
    return {
      tree: data.ingestedTree.tree,
      error,
    }
  })
}

export function retrieveIngestedItem(partnerKey: string, treeKey: string) {
  const query: IQueryInfo = {
    resourceData: {
      key: 'ingestedItem',
      fields: ['tree'],
    },
    params: {
      partner_key: partnerKey,
      ingestion_key: treeKey,
    },
  }
  return doIngestionQueries<{ tree: ICourseNode }>([query], false).then(
    ({ data, error }) => {
      return {
        tree: data.ingestedItem.tree,
        error,
      }
    },
  )
}

export type { ISubject } from 'shared/types/subjects'
import type { ISubject } from 'shared/types/subjects'

interface ISubjectResponse {
  subjects: ISubject[]
}

/**
 * @deprecated
 */
function getSubjects() {
  return getAPIRequest<ISubjectResponse>('/api/subjects').then((resp) => {
    return resp.subjects.reduce((acc: { [key: string]: ISubject }, subject) => {
      acc[subject.subject_name] = subject
      return acc
    }, {})
  })
}

function retrieveIngestionUrlForKey(
  partnerKey: string,
  key: string,
): Promise<string> {
  const query: IQueryInfo = {
    resourceData: {
      key,
      fields: [],
    },
    params: {
      partner_key: partnerKey,
    },
  }
  return doIngestionQueries<string>([query], true).then(({ data }) => {
    return data[key]
  })
}

export function retrieveContentUrl({
  partnerKey,
  subject,
  curricula,
  envOverride,
  extraBit,
}: {
  partnerKey: string
  subject: string
  curricula: string
  envOverride?: string
  extraBit?: string
}) {
  const query: IQueryInfo = {
    resourceData: {
      key: 'contentUploadURL',
      fields: [],
    },
    params: {
      partner_key: partnerKey,
      subject,
      curricula,
      file_name: 'NotNeeded',
    },
  }
  return doIngestionQueries<{ [key: string]: string }>([query], true).then(
    ({ data }) => {
      let url = data[query.resourceData.key] + (extraBit ? `/${extraBit}` : '')
      if (envOverride?.length) {
        url = url.replace(
          'kiddom-media-development',
          `kiddom-media-${envOverride}`,
        )
      }
      return url
    },
  )
}

function uploadPartnerFileForKey({
  partnerKey,
  blob,
  onLoaded,
  urlKey,
  fileName,
}: {
  partnerKey: string
  blob: Blob
  onLoaded?: () => void
  urlKey: UrlKey
  fileName: string
}) {
  return retrieveIngestionUrlForKey(partnerKey, urlKey).then(
    async (uploadUrl) => {
      const parsed = new URL(uploadUrl)
      const bucket = parsed.host.split('.')[0]
      const basePath = parsed.pathname.replace(/^\/+/, '').replace(/\/$/, '')
      const objectName = `${basePath}/${fileName}`

      const presignedUrls = await getPresignedUploadUrls([
        {
          bucket_name: bucket,
          object_name: objectName,
          headers: {
            ContentType: mime.getType(blob.type) ?? '',
          },
        },
      ])

      if (!presignedUrls[0]) {
        throw new Error(`Failed to get presigned URL for file: ${fileName}`)
      }

      await fetch(presignedUrls[0], {
        method: 'PUT',
        body: blob,
      })
      onLoaded?.()
      return fileName
    },
  )
}

async function sendContentFile(fileUrl: string, blob: Blob) {
  return new Promise<string[]>((resolve) => {
    const x = new XMLHttpRequest()
    x.open(PUT, fileUrl)
    x.setRequestHeader('Content-Type', mime.getType(blob.type) ?? '')
    x.onreadystatechange = function () {
      if (x.readyState === 4 && x.status === 200) {
        resolve([fileUrl])
      }
    }
    x.send(blob)
  })
}

async function uploadContentFileOrBlob(
  baseUrl: string,
  blob: Blob,
): Promise<string[]> {
  if (blob instanceof File) {
    return sendContentFile(baseUrl, blob)
  }
  return new Promise((resolve) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(blob as any).file((f: File) => sendContentFile(baseUrl, f).then(resolve))
  })
}

export async function uploadContentFiles(
  idParts: {
    partnerKey: string
    subject: string
    curricula: string
    envOverride?: string
    extraBit?: string
  },
  ...files: File[]
): Promise<{ [fileName: string]: string }> {
  const fileParams = files.map((file) => ({
    blob: file,
    fileName: file.name,
  }))
  return uploadContentBlobsWithName(idParts, ...fileParams)
}

export async function uploadContentBlobsWithName(
  parts: {
    partnerKey: string
    subject: string
    curricula: string
    envOverride?: string
    extraBit?: string
  },
  ...files: { blob: Blob; fileName: string }[]
): Promise<{ [fileName: string]: string }> {
  if (!files.length) return {}
  const contentUrl = new URL((await retrieveContentUrl(parts)).split('?')[0])

  const bucket = contentUrl.host.split('.')[0]
  const keyPrefix = contentUrl.pathname.replace(/^\/+/, '').replace(/\/$/, '')

  const presignItems = files.map(({ blob, fileName }) => ({
    bucket_name: bucket,
    object_name: `${keyPrefix}/${fileName}`,
    headers: {
      ContentType: mime.getType(blob.type) ?? '',
    },
  }))

  const presignedUrls = await getPresignedUploadUrls(presignItems)
  if (!presignedUrls.length) return {}

  const uploadedFiles = await files.reduce<Promise<Array<[string, string]>>>(
    async (acc, { blob, fileName }, index) => {
      const accumulated = await acc

      await uploadContentFileOrBlob(presignedUrls[index], blob)
      const publicUrl = presignedUrls[index].split('?')[0]
      accumulated.push([fileName, publicUrl])
      if (index && index % 50 === 0) {
        // AWS doesn't like when we send too many too quickly
        await new Promise((resolve) => setTimeout(resolve, 3000))
      }
      return accumulated
    },
    Promise.resolve([]),
  )
  return Object.fromEntries(uploadedFiles)
}

export function uploadContentFile({
  partnerKey,
  subject,
  curricula,
  blob,
  fileName,
  envOverride,
}: {
  partnerKey: string
  subject: string
  curricula: string
  blob: Blob
  fileName: string
  envOverride?: string
}): Promise<string> {
  return uploadContentBlobsWithName(
    {
      partnerKey,
      subject,
      curricula,
      envOverride,
    },
    { blob, fileName },
  ).then((files) => Object.values(files)[0])
}

export function contentFiles({
  partnerKey,
  subject,
  curricula,
}: {
  partnerKey: string
  subject: string
  curricula: string
  envOverride?: string
}): Promise<{ [key: string]: AwsContentItem }> {
  const queryInfo: IQueryInfo = {
    resourceData: {
      key: 'contentFiles',
      fields: ['name', { contents: ['key', 'URL'] }],
    },
    params: {
      partner_key: partnerKey,
      subject,
      curricula,
    },
  }
  return doIngestionQueries<AwsListResult>([queryInfo], true).then(
    ({ data: { contentFiles } }) => {
      return retrieveContentUrl({
        partnerKey,
        subject,
        curricula,
      }).then((url) => {
        const mapped: { [key: string]: AwsContentItem } = {}
        contentFiles.contents.forEach((res) => {
          const fileNamePart = res.key.split('/').pop()
          res.URL = `${url.split('?')[0]}/${fileNamePart}`
          res.key = fileNamePart!
          mapped[res.key] = res
        })
        return mapped
      })
    },
  )
}

export function isIngestedSource(source: string): boolean {
  return (
    source.startsWith('https://api-assignment-assets-production.s3') ||
    source.includes('kiddom-media')
  )
}

export function createTreeFromIngestion(params: {
  partner_key: string
  ingestion_key: string
  user_id?: number
}) {
  const query: IQueryInfo = {
    resourceData: {
      key: 'createTreeFromIngestion',
      fields: [{ curricula: ['id', 'tree_id', 'tree_version'] }],
    },
    params,
  }
  return doIngestionQueries<{
    curricula: { id: number; tree_id: string; tree_version: string }
  }>([query], true).then(({ data }) => {
    return data[query.resourceData.key]
  })
}

export function domainFromSource(source: string) {
  try {
    const url = new URL(source)
    return url.hostname
  } catch (e: any) {}
}

export function sortedUrls(urls: string[]) {
  return urls.reduce(
    (
      acc: {
        withoutDomain: string[]
        byDomain: { [domain: string]: string[] }
      },
      url,
    ) => {
      const domain = domainFromSource(url)
      if (domain) {
        if (!acc.byDomain[domain]) {
          acc.byDomain[domain] = []
        }
        acc.byDomain[domain].push(url)
      } else {
        acc.withoutDomain.push(url)
      }
      return acc
    },
    { withoutDomain: [], byDomain: {} },
  )
}
